import { useCallback, useState } from 'react';
import { CriteriaSetupModal } from '../components/CriteriaSetupModal';
import { Link, useNavigate } from 'react-router-dom';
import {
  useDashboardMetrics,
  useAiCostMetrics,
  useAICosts,
  useDeadLetterStats,
  useScreeningConflicts,
  useDashboardJobs,
} from '../hooks/useApi';
import AiAccuracyWidget from '../components/AiAccuracyWidget';
import { api } from '../api/client';
import {
  Briefcase,
  ChevronRight,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import StatusChart from '../components/StatusChart';
import RecentActivityTimeline from '../components/RecentActivityTimeline';
import AiCostSummary from '../components/AiCostSummary';
import AICostDashboard from '../components/AICostDashboard';
import { ArbieMascot } from '../components/ArbieMascot';
import { SignalsBadge } from '../components/SignalsBadge';
import type { ApplicationStatus } from '../types';
import { useAuth } from '../hooks/useAuth';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function openCommandPalette() {
  window.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true })
  );
}

type AttentionType = 'error' | 'conflict' | 'stale' | 'no-criteria';

interface AttentionItem {
  id: string;
  type: AttentionType;
  title: string;
  sub: string;
  onClick: () => void;
  /** For no-criteria cards: the job to open in CriteriaSetupModal */
  jobRef?: { id: number; title: string };
}

const borderColorByType: Record<AttentionType, string> = {
  error: 'border-red-500',
  conflict: 'border-orange-500',
  stale: 'border-amber-500',
  'no-criteria': 'border-gray-500',
};

const chipColorByType: Record<AttentionType, string> = {
  error: 'bg-red-900/60 text-red-300',
  conflict: 'bg-orange-900/60 text-orange-300',
  stale: 'bg-amber-900/60 text-amber-300',
  'no-criteria': 'bg-gray-700 text-gray-300',
};

const chipLabelByType: Record<AttentionType, string> = {
  error: 'Error',
  conflict: 'Conflict',
  stale: 'Stale',
  'no-criteria': 'No Criteria',
};

/** US-644 — how many job cards the dashboard shows before linking to /jobs. */
const MAX_ACTIVE_JOB_CARDS = 6;

const stageConfig = {
  LEAD: { label: 'LEAD', color: 'text-gray-400', dot: 'bg-gray-400' },
  SCREENING: { label: 'SCREENING', color: 'text-blue-400', dot: 'bg-blue-400' },
  INTERVIEW: { label: 'INTERVIEW', color: 'text-amber-400', dot: 'bg-amber-400' },
  OFFER: { label: 'OFFER', color: 'text-purple-400', dot: 'bg-purple-400' },
} as const;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showSystem, setShowSystem] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncResult, setSyncResult] = useState<{ success: number; failed: number } | null>(null);
  const [criteriaModalJob, setCriteriaModalJob] = useState<{ id: number; title: string } | null>(null);

  const { data: metricsData, isLoading: metricsLoading } = useDashboardMetrics();
  const { data: aiCostData } = useAiCostMetrics();
  const { data: aiCostDashboardData, isLoading: aiCostDashboardLoading } = useAICosts();
  const { data: dlqStats } = useDeadLetterStats();
  const { data: conflictsData } = useScreeningConflicts();
  const { data: dashboardJobsData, isLoading: jobsLoading } = useDashboardJobs();

  const dlqPending = dlqStats?.byStatus?.pending ?? 0;
  const jobs = dashboardJobsData?.jobs ?? [];
  // US-644: glance-and-jump. 28 full-width rows is the same defect as 89 New
  // Matches rows — show the top of the list and link onward to /jobs.
  const visibleJobs = jobs.slice(0, MAX_ACTIVE_JOB_CARDS);
  const hiddenJobCount = jobs.length - visibleJobs.length;

  // Build attention items list
  const attentionItems: AttentionItem[] = [];
  if (dlqPending > 0) {
    attentionItems.push({
      id: 'dlq',
      type: 'error',
      title: `${dlqPending} error${dlqPending !== 1 ? 's' : ''} in queue`,
      sub: 'Review failed jobs in Settings → System Health',
      onClick: () => navigate('/settings', { state: { tab: 'system-health' } }),
    });
  }
  if (conflictsData?.conflicts) {
    for (const c of conflictsData.conflicts) {
      attentionItems.push({
        id: `conflict-${c.reviewId}`,
        type: 'conflict',
        title: c.candidateName || 'Unknown Candidate',
        sub: `${c.jobTitle || 'Unknown Job'} · Score ${c.overallScore} with ${c.knockoutCount || 1} knockout${(c.knockoutCount || 1) !== 1 ? 's' : ''}`,
        onClick: () => navigate(`/reviews/${c.reviewId}`),
      });
    }
  }

  const handleStatusClick = useCallback(
    (status: ApplicationStatus) => {
      navigate(`/applications?status=${status}`);
    },
    [navigate]
  );

  const handleSyncAllJobs = async () => {
    if (syncingAll) return;
    setSyncingAll(true);
    setSyncResult(null);
    let success = 0;
    let failed = 0;
    try {
      const allJobs = await api.getJobs(1, 100, undefined, 'active');
      for (const job of allJobs.data) {
        try {
          await api.triggerJobSync(job.id);
          success++;
        } catch {
          failed++;
        }
      }
    } catch {
      failed++;
    }
    setSyncResult({ success, failed });
    setSyncingAll(false);
    setTimeout(() => setSyncResult(null), 5000);
  };

  const rawByStatus = metricsData?.applications?.byStatus || {};
  const normalizedByStatus: Record<string, number> = {};
  for (const [key, val] of Object.entries(rawByStatus)) {
    const lower = key.toLowerCase();
    if (lower === 'stub') continue;
    normalizedByStatus[lower] = (normalizedByStatus[lower] || 0) + Number(val);
  }
  const applicationsByStatus: Record<ApplicationStatus, number> = {
    received: normalizedByStatus.received || 0,
    enriching: normalizedByStatus.enriching || 0,
    enriched: normalizedByStatus.enriched || 0,
    ai_processing: normalizedByStatus.ai_processing || 0,
    ai_complete: normalizedByStatus.ai_complete || 0,
    synced: normalizedByStatus.synced || 0,
    error: normalizedByStatus.error || 0,
    stub: normalizedByStatus.stub || 0,
    skipped: normalizedByStatus.skipped || 0,
  };

  const firstName = user?.name?.split(' ')[0] ?? 'there';

  // Subline counts: total reviewed recently from metricsData if available
  const recentlyReviewed = metricsData?.recentActivity?.length ?? 0;
  const needsEyes = attentionItems.length;

  return (
    <div className="space-y-6">
      {/* Greeting Row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Left: mascot + greeting */}
        <div className="flex items-center gap-3">
          <ArbieMascot size="md" className="w-[46px] h-[46px] flex-shrink-0" />
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-gray-100">
              {getGreeting()}, {firstName}
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">
              {recentlyReviewed > 0
                ? `Arbie reviewed ${recentlyReviewed} candidate${recentlyReviewed !== 1 ? 's' : ''} recently${needsEyes > 0 ? ` — ${needsEyes} need your eyes` : ''}`
                : 'Your recruiting pipeline'}
            </p>
          </div>
        </div>

        {/* Right: Cmd+K trigger bar */}
        <button
          onClick={openCommandPalette}
          className="bg-gray-800 border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-2 cursor-pointer hover:border-amber-600/50 transition-colors min-w-[220px]"
          aria-label="Open command palette"
        >
          <Sparkles className="h-4 w-4 text-amber-500 flex-shrink-0" />
          <span className="text-sm text-gray-400 flex-1 text-left">Force AI review or search…</span>
          <kbd className="text-xs bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded font-mono">⌘K</kbd>
        </button>
      </div>

      {/* Needs Attention */}
      {attentionItems.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-xs font-semibold text-gray-400 tracking-wider uppercase">
              Needs Attention
            </span>
            <span className="ml-1 bg-red-600 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full">
              {attentionItems.length}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {attentionItems.map((item) => (
              <button
                key={item.id}
                onClick={item.type === 'no-criteria' && item.jobRef
                  ? () => setCriteriaModalJob(item.jobRef!)
                  : item.onClick}
                className={`bg-gray-800 rounded-xl border-l-4 ${borderColorByType[item.type]} p-4 cursor-pointer hover:-translate-y-0.5 transition-transform text-left w-full`}
              >
                <div className="mb-1.5">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${chipColorByType[item.type]}`}>
                    {chipLabelByType[item.type]}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-100 mt-1">{item.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">{item.sub}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Background Signals — new candidate matches */}
      <SignalsBadge />

      {/* My Active Jobs */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-base font-semibold text-gray-100">My Active Jobs</h3>
          <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full">
            {jobs.length}
          </span>
        </div>

        {jobsLoading ? (
          <div className="flex flex-col gap-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-800 border border-white/[0.07] rounded-2xl px-5 py-4 animate-pulse">
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-2" />
                <div className="h-3 bg-gray-700 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="bg-gray-800 border border-white/[0.07] rounded-2xl p-8 text-center">
            <Briefcase className="mx-auto h-10 w-10 text-gray-600 mb-3" />
            <p className="text-gray-400 font-medium">No active jobs</p>
            <p className="text-sm text-gray-500 mt-1">Jobs will appear here when they are active in SmartRecruiters</p>
          </div>
        ) : (
          <>
          <div
            data-testid="active-jobs-grid"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-3"
          >
            {visibleJobs.map((job) => (
              <button
                key={job.id}
                data-testid="active-job-card"
                onClick={() => navigate(`/jobs/${job.id}`)}
                className="bg-gray-800 border border-white/[0.07] rounded-2xl px-5 py-4 flex flex-col gap-3 hover:border-amber-600/45 hover:-translate-y-0.5 transition-all cursor-pointer text-left w-full"
              >
                {/* Left side */}
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-100 truncate">{job.title}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="inline-flex items-center gap-1 text-xs text-green-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                      Active
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {job.department ? `${job.department} · ` : ''}
                    {job.applicationCount} candidate{job.applicationCount !== 1 ? 's' : ''}
                    {job.reviewedTodayCount > 0 ? ` · ${job.reviewedTodayCount} reviewed today` : ''}
                  </p>
                </div>

                {/* Stage chips — below the title so a 2-up card keeps all four
                    chips legible. Two-up only starts at `md` (768px): `sm` is
                    640px, which is inside the 600–700px extension window where
                    the grid must stay single-column. */}
                <div className="flex items-center gap-2 flex-wrap">
                  {(Object.keys(stageConfig) as Array<keyof typeof stageConfig>).map((stage) => (
                    <div
                      key={stage}
                      className="bg-gray-950 rounded-xl px-3 py-2 flex flex-col items-center min-w-[52px]"
                    >
                      <span className={`text-lg font-bold leading-none ${stageConfig[stage].color}`}>
                        {job.stageCounts?.[stage] ?? 0}
                      </span>
                      <span className="flex items-center gap-0.5 mt-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${stageConfig[stage].dot} flex-shrink-0`} />
                        <span className={`text-[10px] uppercase font-medium ${stageConfig[stage].color}`}>
                          {stage}
                        </span>
                      </span>
                    </div>
                  ))}
                  <ChevronRight className="h-4 w-4 text-gray-500 ml-1" />
                </div>
              </button>
            ))}
          </div>
          {/* Sits on the page background, not inside a dark card: amber-400 on
              light-mode bg-surface-50 (#fafaf9) computes to 1.60:1. */}
          {hiddenJobCount > 0 && (
            <Link
              to="/jobs"
              className="inline-block mt-3 text-xs text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300"
            >
              View all {jobs.length} →
            </Link>
          )}
          </>
        )}
      </section>

      {/* Recent Activity */}
      <section>
        <h3 className="text-base font-semibold text-gray-100 mb-3">Recent Activity</h3>
        <div className="bg-gray-800 border border-white/[0.07] rounded-2xl p-6">
          {metricsLoading ? (
            <div className="flex h-48 items-center justify-center text-gray-500">Loading…</div>
          ) : metricsData?.recentActivity && metricsData.recentActivity.length > 0 ? (
            <RecentActivityTimeline activities={metricsData.recentActivity} />
          ) : (
            <div className="flex h-48 items-center justify-center text-gray-500">No recent activity</div>
          )}
        </div>
      </section>

      {/* System & Analytics — collapsed by default */}
      <section>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setShowSystem(!showSystem)}
          onKeyDown={(e) => e.key === 'Enter' || e.key === ' ' ? setShowSystem(!showSystem) : undefined}
          className="w-full bg-gray-800 border border-white/[0.07] rounded-2xl px-5 py-3.5 flex items-center justify-between hover:bg-gray-750 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-700">
              <DollarSign className="h-4 w-4 text-gray-400" />
            </div>
            <span className="text-sm font-medium text-gray-300">System &amp; Analytics</span>
            {syncResult && (
              <span className={`text-xs ${syncResult.failed > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                Queued {syncResult.success} job{syncResult.success !== 1 ? 's' : ''}
                {syncResult.failed > 0 && `, ${syncResult.failed} failed`}
              </span>
            )}
            {aiCostDashboardData && (
              <span className="text-xs text-gray-500">
                ${aiCostDashboardData.budget.used.toFixed(2)} / ${aiCostDashboardData.budget.daily.toFixed(2)} today
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); handleSyncAllJobs(); }}
              disabled={syncingAll}
              className="text-xs text-gray-400 hover:text-gray-200 flex items-center gap-1 transition-colors"
              aria-label="Sync all jobs"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${syncingAll ? 'animate-spin' : ''}`} />
              {syncingAll ? 'Syncing…' : 'Sync All'}
            </button>
            {showSystem ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </div>

        {showSystem && (
          <div className="mt-3 space-y-6">
            {/* Application pipeline status */}
            <div className="bg-gray-800 border border-white/[0.07] rounded-2xl">
              <div className="border-b border-white/[0.07] px-5 py-3">
                <h4 className="text-sm font-medium text-gray-300">Pipeline Status</h4>
              </div>
              <div className="p-5">
                {metricsLoading ? (
                  <div className="flex h-48 items-center justify-center text-gray-500">Loading…</div>
                ) : metricsData?.applications?.byStatus ? (
                  <StatusChart data={applicationsByStatus} onStatusClick={handleStatusClick} />
                ) : (
                  <div className="flex h-48 items-center justify-center text-gray-500">No data</div>
                )}
              </div>
            </div>

            {/* AI Cost Dashboard */}
            <div className="bg-gray-800 border border-white/[0.07] rounded-2xl">
              <div className="border-b border-white/[0.07] px-5 py-3">
                <h4 className="text-sm font-medium text-gray-300">AI Cost Dashboard</h4>
              </div>
              <div className="p-5 space-y-6">
                {aiCostDashboardLoading ? (
                  <div className="flex h-32 items-center justify-center text-gray-500">Loading…</div>
                ) : aiCostDashboardData ? (
                  <AICostDashboard data={aiCostDashboardData} />
                ) : (
                  <div className="flex h-32 items-center justify-center text-gray-500">
                    No cost data yet — appears after reviews are processed
                  </div>
                )}
                {aiCostData && (
                  <div className="border-t border-white/[0.07] pt-5">
                    <h5 className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wide">
                      Pipeline Request Metrics
                    </h5>
                    <AiCostSummary data={aiCostData} />
                  </div>
                )}
              </div>
            </div>

            {/* AI Accuracy */}
            <AiAccuracyWidget />
          </div>
        )}
      </section>

      {/* Criteria setup modal — opened from no-criteria attention cards */}
      {criteriaModalJob && (
        <CriteriaSetupModal
          jobId={criteriaModalJob.id}
          jobTitle={criteriaModalJob.title}
          isOpen={true}
          onClose={() => setCriteriaModalJob(null)}
        />
      )}
    </div>
  );
}
